/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { OPTIMIZATION_CAPABILITY_STRINGS } from '@Optimization/Constants/DxpCapabilityStrings.constants';
import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  ICON_CAPABILITY_ADMINISTRATION,
  ICON_CAPABILITY_ADVANCED_FORMS,
  ICON_CAPABILITY_COMPONENT_SERVICE,
  ICON_CAPABILITY_CONFIGURATION,
  ICON_CAPABILITY_CONTENT_MANAGEMENT,
  ICON_CAPABILITY_CUSTOMER_DATA_PLATFORM,
  ICON_CAPABILITY_DATA_SERVICES,
  ICON_CAPABILITY_INTEGRATIONS,
  ICON_CAPABILITY_MONITORING,
  ICON_CAPABILITY_OPTIMIZATION,
  ICON_CAPABILITY_SEARCH,
} from '@squiz/sds';
import { CONVERSATIONS_CAPABILITY_STRINGS } from 'apps/artificial-intelligence/src/Constants/DxpCapabilityStrings.constants';
import { COMPONENT_SERVICE_CAPABILITY_STRINGS } from 'apps/component-service/src/Constants/DxpCapabilityStrings.constants';
import { CONFIGURATION_CAPABILITY_STRINGS } from 'apps/configuration/src/Constants/DxpCapabilityStrings.constants';
import { CDP_CAPABILITY_STRINGS } from 'apps/customer-data-platform/src/Constants/DxpCapabilityStrings.constants';
import { DAM_CAPABILITY_STRINGS } from 'apps/digital-asset-management/src/Constants/DxpCapabilityStrings.constants';
import { MONITORING_CAPABILITY_STRINGS } from 'apps/monitoring/src/Constants/DxpCapabilityStrings.constants';
import { ICON_CAPABILITY_CONVERSATIONS, ICON_CAPABILITY_DIGITAL_ASSET_MANAGEMENT } from 'libs/sds/src/Icons';
import { ConsoleInstanceType } from '../../Services/console';
import { getOrganisationPath } from '../../Utilities/GetOrganisationPath/GetOrganisationPath';
import { DXP_EXTERNAL_LINKS } from '../ExternalLinks.constants';
import { TENANT_FEATURE_FLAGS } from '../FeatureFlags.constants';
import {
  ADMIN_CAPABILITY_STRINGS,
  ADVANCED_FORMS_CAPABILITY_STRINGS,
  CONTENT_CAPABILITY_STRINGS,
  DATA_SERVICES_CAPABILITY_STRINGS,
  INTEGRATIONS_CAPABILITY_STRINGS,
  SEARCH_CAPABILITY_STRINGS,
} from './DxpCapabilityStrings.constants';
import { DxpCapabilityItem } from './defs/DxpCapabilityItem.defs';

export const ADMINISTRATION: DxpCapabilityItem = {
  altTitle: ADMIN_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: ADMIN_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      ADMIN_CAPABILITY_STRINGS.dialogBullet1,
      ADMIN_CAPABILITY_STRINGS.dialogBullet2,
      ADMIN_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  icon: ICON_CAPABILITY_ADMINISTRATION,
  key: `administration`,
  path: getOrganisationPath(`/administration`),
  permission: PERMISSIONS.dxpConsole.TENANT_UPDATE,
  subtitle: ADMIN_CAPABILITY_STRINGS.subtitle,
  title: ADMIN_CAPABILITY_STRINGS.title,
};

export const ADVANCED_FORMS: DxpCapabilityItem = {
  altTitle: ADVANCED_FORMS_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: ADVANCED_FORMS_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      ADVANCED_FORMS_CAPABILITY_STRINGS.dialogBullet1,
      ADVANCED_FORMS_CAPABILITY_STRINGS.dialogBullet2,
      ADVANCED_FORMS_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  featureFlag: TENANT_FEATURE_FLAGS.FORMS_ACCESS,
  icon: ICON_CAPABILITY_ADVANCED_FORMS,
  key: `advanced-forms`,
  path: getOrganisationPath(`/advanced-forms`),
  subtitle: ADVANCED_FORMS_CAPABILITY_STRINGS.subtitle,
  title: ADVANCED_FORMS_CAPABILITY_STRINGS.title,
};

export const COMPONENT_SERVICE: DxpCapabilityItem = {
  altTitle: COMPONENT_SERVICE_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: COMPONENT_SERVICE_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      COMPONENT_SERVICE_CAPABILITY_STRINGS.dialogBullet1,
      COMPONENT_SERVICE_CAPABILITY_STRINGS.dialogBullet2,
      COMPONENT_SERVICE_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  featureFlag: TENANT_FEATURE_FLAGS.COMPONENT_SERVICE_ACCESS,
  icon: ICON_CAPABILITY_COMPONENT_SERVICE,
  key: `component-service`,
  path: getOrganisationPath(`/component-service`),
  subtitle: COMPONENT_SERVICE_CAPABILITY_STRINGS.subtitle,
  title: COMPONENT_SERVICE_CAPABILITY_STRINGS.title,
};

export const CONFIGURATION: DxpCapabilityItem = {
  altTitle: CONFIGURATION_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: CONFIGURATION_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      CONFIGURATION_CAPABILITY_STRINGS.dialogBullet1,
      CONFIGURATION_CAPABILITY_STRINGS.dialogBullet2,
      CONFIGURATION_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  icon: ICON_CAPABILITY_CONFIGURATION,
  key: `configuration`,
  path: getOrganisationPath(`/configuration`),
  subtitle: CONFIGURATION_CAPABILITY_STRINGS.subtitle,
  title: CONFIGURATION_CAPABILITY_STRINGS.title,
};

export const CONTENT_MANAGEMENT: DxpCapabilityItem = {
  altTitle: CONTENT_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: CONTENT_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      CONTENT_CAPABILITY_STRINGS.dialogBullet1,
      CONTENT_CAPABILITY_STRINGS.dialogBullet2,
      CONTENT_CAPABILITY_STRINGS.dialogBullet3,
    ],
    docLink: DXP_EXTERNAL_LINKS.matrixProductPage,
  },
  icon: ICON_CAPABILITY_CONTENT_MANAGEMENT,
  key: `content-management`,
  path: getOrganisationPath(`/content-management`),
  productName: CONTENT_CAPABILITY_STRINGS.productName,
  subscription: ConsoleInstanceType.matrix,
  subtitle: CONTENT_CAPABILITY_STRINGS.subtitle,
  title: CONTENT_CAPABILITY_STRINGS.title,
};

export const CONVERSATIONS: DxpCapabilityItem = {
  altTitle: CONVERSATIONS_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: CONVERSATIONS_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      CONVERSATIONS_CAPABILITY_STRINGS.dialogBullet1,
      CONVERSATIONS_CAPABILITY_STRINGS.dialogBullet2,
      CONVERSATIONS_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  featureFlag: TENANT_FEATURE_FLAGS.CONVERSATIONS_ACCESS,
  icon: ICON_CAPABILITY_CONVERSATIONS,
  key: `conversations`,
  path: getOrganisationPath(`/conversations`),
  permission: PERMISSIONS.dxpAi.CHATBOT_READ,
  subtitle: CONVERSATIONS_CAPABILITY_STRINGS.subtitle,
  title: CONVERSATIONS_CAPABILITY_STRINGS.title,
};

export const CUSTOMER_DATA_PLATFORM: DxpCapabilityItem = {
  altTitle: CDP_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: CDP_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      CDP_CAPABILITY_STRINGS.dialogBullet1,
      CDP_CAPABILITY_STRINGS.dialogBullet2,
      CDP_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  icon: ICON_CAPABILITY_CUSTOMER_DATA_PLATFORM,
  key: `customer-data-platform`,
  path: getOrganisationPath(`/cdp`),
  subtitle: CDP_CAPABILITY_STRINGS.subtitle,
  title: CDP_CAPABILITY_STRINGS.title,
};

export const DATA_SERVICES: DxpCapabilityItem = {
  altTitle: DATA_SERVICES_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: DATA_SERVICES_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      DATA_SERVICES_CAPABILITY_STRINGS.dialogBullet1,
      DATA_SERVICES_CAPABILITY_STRINGS.dialogBullet2,
      DATA_SERVICES_CAPABILITY_STRINGS.dialogBullet3,
    ],
    docLink: DXP_EXTERNAL_LINKS.datastoreProductPage,
  },
  icon: ICON_CAPABILITY_DATA_SERVICES,
  key: `data-services`,
  path: getOrganisationPath(`/data-services`),
  productName: DATA_SERVICES_CAPABILITY_STRINGS.productName,
  subtitle: DATA_SERVICES_CAPABILITY_STRINGS.subtitle,
  title: DATA_SERVICES_CAPABILITY_STRINGS.title,
};

export const DIGITAL_ASSET_MANAGEMENT: DxpCapabilityItem = {
  altTitle: DAM_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: DAM_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      DAM_CAPABILITY_STRINGS.dialogBullet1,
      DAM_CAPABILITY_STRINGS.dialogBullet2,
      DAM_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  icon: ICON_CAPABILITY_DIGITAL_ASSET_MANAGEMENT,
  key: `digital-asset-management`,
  path: getOrganisationPath(`/digital-asset-management`),
  subtitle: DAM_CAPABILITY_STRINGS.subtitle,
  title: DAM_CAPABILITY_STRINGS.title,
};

export const INTEGRATIONS: DxpCapabilityItem = {
  altTitle: INTEGRATIONS_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: INTEGRATIONS_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      INTEGRATIONS_CAPABILITY_STRINGS.dialogBullet1,
      INTEGRATIONS_CAPABILITY_STRINGS.dialogBullet2,
      INTEGRATIONS_CAPABILITY_STRINGS.dialogBullet3,
    ],
    docLink: DXP_EXTERNAL_LINKS.connectProductPage,
  },
  icon: ICON_CAPABILITY_INTEGRATIONS,
  key: `integrations`,
  path: getOrganisationPath(`/integrations`),
  productName: INTEGRATIONS_CAPABILITY_STRINGS.productName,
  subtitle: INTEGRATIONS_CAPABILITY_STRINGS.subtitle,
  title: INTEGRATIONS_CAPABILITY_STRINGS.title,
};

export const MONITORING: DxpCapabilityItem = {
  altTitle: MONITORING_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: MONITORING_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      MONITORING_CAPABILITY_STRINGS.dialogBullet1,
      MONITORING_CAPABILITY_STRINGS.dialogBullet2,
      MONITORING_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  icon: ICON_CAPABILITY_MONITORING,
  key: `monitoring`,
  path: getOrganisationPath(`/monitoring`),
  permission: PERMISSIONS.dxpLogging.LOGS_READ,
  subtitle: MONITORING_CAPABILITY_STRINGS.subtitle,
  title: MONITORING_CAPABILITY_STRINGS.title,
};

export const OPTIMIZATION: DxpCapabilityItem = {
  altTitle: OPTIMIZATION_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: OPTIMIZATION_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      OPTIMIZATION_CAPABILITY_STRINGS.dialogBullet1,
      OPTIMIZATION_CAPABILITY_STRINGS.dialogBullet2,
      OPTIMIZATION_CAPABILITY_STRINGS.dialogBullet3,
    ],
  },
  icon: ICON_CAPABILITY_OPTIMIZATION,
  key: `optimization`,
  path: getOrganisationPath(`/optimization`),
  subtitle: OPTIMIZATION_CAPABILITY_STRINGS.subtitle,
  title: OPTIMIZATION_CAPABILITY_STRINGS.title,
};

export const SEARCH: DxpCapabilityItem = {
  altTitle: SEARCH_CAPABILITY_STRINGS.altTitle,
  dialogContent: {
    blurb: SEARCH_CAPABILITY_STRINGS.dialogBlurb,
    bullets: [
      SEARCH_CAPABILITY_STRINGS.dialogBullet1,
      SEARCH_CAPABILITY_STRINGS.dialogBullet2,
      SEARCH_CAPABILITY_STRINGS.dialogBullet3,
    ],
    docLink: DXP_EXTERNAL_LINKS.funnelbackProductPage,
  },
  icon: ICON_CAPABILITY_SEARCH,
  key: `search`,
  path: getOrganisationPath(`/search`),
  productName: SEARCH_CAPABILITY_STRINGS.productName,
  subtitle: SEARCH_CAPABILITY_STRINGS.subtitle,
  title: SEARCH_CAPABILITY_STRINGS.title,
};

/**
 * All active DXP capability items.
 */
export const DXP_CAPABILITY_ITEMS: Array<DxpCapabilityItem> = [
  CONTENT_MANAGEMENT,
  COMPONENT_SERVICE,
  ADVANCED_FORMS,
  CONVERSATIONS,
  SEARCH,
  OPTIMIZATION,
  CUSTOMER_DATA_PLATFORM,
  INTEGRATIONS,
  DATA_SERVICES,
  DIGITAL_ASSET_MANAGEMENT,
  MONITORING,
  CONFIGURATION,
  ADMINISTRATION,
];
